import React, { useState, useMemo } from 'react';
import { List, useTable, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Image, Upload, Button, message } from 'antd';
import { SearchOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { AdminMediaAssetListItemDto, MediaType } from '@wellverse/shared-ts';
import { API_BASE_URL } from '../../providers/dataProvider';
import { GpsLocation } from '../../components/GpsLocation';


const mediaTypeColors: Record<MediaType, string> = {
  [MediaType.IMAGE]: '#3ecf8e',
  [MediaType.VIDEO]: '#f5a623',
  [MediaType.DOCUMENT]: '#1890ff',
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export const MediaAssetList: React.FC = () => {
  const { tableProps, tableQueryResult } = useTable<AdminMediaAssetListItemDto>({
    resource: 'media-assets',
    pagination: { current: 1, pageSize: 20 },
  });

  const [searchText, setSearchText] = useState('');

  const filteredData = useMemo(() => {
    if (!searchText) return tableProps.dataSource;
    const lower = searchText.toLowerCase();
    return tableProps.dataSource?.filter((item: any) =>
      item.originalName?.toLowerCase().includes(lower) ||
      item.fileName?.toLowerCase().includes(lower) ||
      item.userId?.toLowerCase().includes(lower)
    );
  }, [tableProps.dataSource, searchText]);

  const customRequest = async ({ file, onSuccess, onError }: any) => {
    const authToken = localStorage.getItem('access_token');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_BASE_URL}/media/upload`, formData, {
        headers: {
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          'Content-Type': 'multipart/form-data',
        },
      });
      onSuccess?.(res.data);
      message.success('Upload successful');
      tableQueryResult.refetch();
    } catch (err: any) {
      onError?.(err);
      message.error(err.response?.data?.message || 'Upload failed');
    }
  };

  return (
    <List resource="media-assets">
      <div style={{ marginBottom: 24, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <Upload customRequest={customRequest} showUploadList={false}>
          <Button type="primary" icon={<UploadOutlined />}>
            Upload File
          </Button>
        </Upload>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by file name or user ID..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
          allowClear
        />
      </div>
      <Table {...tableProps} dataSource={filteredData} rowKey="id">
        <Table.Column
          title="Preview"
          dataIndex="url"
          render={(url: string, record: AdminMediaAssetListItemDto) =>
            record.mediaType === MediaType.IMAGE ? (
              <Image src={url} alt={record.originalName} width={60} style={{ borderRadius: 4 }} />
            ) : (
              <Tag>{record.mediaType}</Tag>
            )
          }
        />
        <Table.Column dataIndex="originalName" title="Original Name" />
        <Table.Column dataIndex="fileName" title="File Name" />
        <Table.Column
          dataIndex="mediaType"
          title="Type"
          filters={Object.values(MediaType).map((t) => ({ text: t, value: t }))}
          onFilter={(value, record: any) => record.mediaType === value}
          render={(type: MediaType) => (
            <Tag
              style={{
                borderColor: mediaTypeColors[type] || '#898989',
                color: mediaTypeColors[type] || '#898989',
                background: `${mediaTypeColors[type] || '#898989'}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {type}
            </Tag>
          )}
        />
        <Table.Column dataIndex="mimeType" title="MIME Type" />
        <Table.Column
          dataIndex="fileSize"
          title="Size"
          render={(size: number) => formatBytes(size)}
        />
        <Table.Column dataIndex="storageProvider" title="Storage" />
        <Table.Column
          title="Location"
          width={160}
          render={(_: unknown, record: AdminMediaAssetListItemDto) => {
            const meta = (record.metadata ?? {}) as {
              latitude?: number;
              longitude?: number;
            };
            return (
              <GpsLocation
                variant="cell"
                latitude={meta.latitude}
                longitude={meta.longitude}
              />
            );
          }}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_, record: AdminMediaAssetListItemDto) => (
            <Space>
              <a href={record.url} target="_blank" rel="noreferrer">
                View
              </a>
              <DeleteButton hideText size="small" recordItemId={record.id} resource="media-assets" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
